import styled from 'styled-components';
import COLORS from '../../style/universal.color';
import { LinkProps } from '../Link/link.types';
import { PlayerMatchupTypes } from './playerMatchup.types';
import FONTS from '../../style/fonts';

const { mlbGrey6, linkBlue, linkBlueOnDark, mlbWhite, mlbGrey5, mlbBlack } = COLORS;

interface PlayerMatchupWrapperProps {
  alignment: 'left' | 'right';
  dark: boolean;
  linkProps?: LinkProps;
  name?: string;
  stacked: boolean;
  isListFormat: boolean;
  isVisualFormat: boolean;
}

export const PlayerMatchupWrapper = styled.div<PlayerMatchupWrapperProps>`
  display: flex;
  ${({ isVisualFormat = false, isListFormat = false }) =>
    isVisualFormat || isListFormat ? 'align-items: center;' : ''}
  font-family: ${FONTS.primaryFont};
  ${({ stacked, alignment }) =>
    !stacked ? `flex-direction: ${alignment === 'left' ? 'row' : 'row-reverse'};` : 'flex-direction: column;'}

  text-align: ${({ alignment }) => alignment};

  > div:last-child > div:first-child > div {
    border-bottom: 1px dotted transparent;
  }

  &:hover {
    > div:last-child > div:first-child > div {
      ${({ linkProps, dark, name }) =>
        linkProps && linkProps.url && name ? `border-bottom: 1px dotted ${dark ? linkBlueOnDark : linkBlue}` : ''};
    }
  }
`;

interface PlayerDataProps {
  alignment: 'left' | 'right';
  simpleInline?: boolean;
  stacked: boolean;
  playerStatsInline: boolean;
  isListFormat: boolean;
  isVisualFormat: boolean;
}

export const PlayerDataWrapper = styled.div<PlayerDataProps>`
  margin-left: ${({ alignment, simpleInline, stacked, isListFormat }) =>
    isListFormat ? '20px' : alignment === 'left' && !simpleInline && !stacked ? '.5em' : 0};
  margin-right: ${({ alignment, isVisualFormat }) => (alignment === 'left' || isVisualFormat ? 0 : '.5em')};
  font-size: 12px;
  display: ${({ simpleInline, playerStatsInline }) => (simpleInline || playerStatsInline ? 'flex' : 'block')};
  align-items: center;
`;

interface PlayerInfoProps {
  alignment: 'left' | 'right';
  dark: boolean;
  type: PlayerMatchupTypes;
  isListFormat: boolean;
  isVisualFormat: boolean;
  useDecisionBox: boolean;
}

export const PlayerInfoWrapper = styled.div<PlayerInfoProps>`
  color: ${({ dark }) => (dark ? mlbWhite : mlbGrey6)};
  display: ${({ type, isVisualFormat = false }) =>
    type === PlayerMatchupTypes.PITCHERPREGAME && !isVisualFormat ? 'block' : 'flex'};
  flex-direction: ${({ alignment }) => (alignment === 'left' ? 'row' : 'row-reverse')};
  align-items: center;
  ${({ useDecisionBox }) => (useDecisionBox ? 'padding-bottom: .2em;' : '')}
  ${({ isVisualFormat }) =>
    isVisualFormat
      ? `
      justify-content: center;
      margin: 8px 0 6px;
    `
      : ''}
`;

interface PlayerStatsWrapperProps {
  simpleInline?: boolean;
  playerStatsInline: boolean;
  isListFormat: boolean;
  isVisualFormat: boolean;
}

export const PlayerStatsWrapper = styled.div<PlayerStatsWrapperProps>`
  color: ${mlbGrey5};
  font-weight: 600;
  margin-left: ${({ simpleInline, playerStatsInline }) => (simpleInline || playerStatsInline ? '3px' : 0)};
  ${({ isVisualFormat = false }) => (isVisualFormat ? 'text-align: center;' : '')}
  ${({ isListFormat = false }) =>
    isListFormat
      ? `
      color: ${mlbGrey5};
      font-weight: bold;
      font-size: 15px;
      margin-top: 3px;
    `
      : ''}
`;

interface PlayerNameWrapperProps {
  simpleInline: boolean;
  isListFormat: boolean;
  isVisualFormat: boolean;
  type: PlayerMatchupTypes;
  'data-mlb-test'?: string;
  'data-testid'?: string;
}

// adding a border bottom here because on hover it gets one and it moves up and down 1px otherwise
// add margin-top when inline so it stays in alignment
export const PlayerNameWrapper = styled.div.attrs<PlayerNameWrapperProps>({
  'data-mlb-test': 'playerNameLinks',
  'data-testid': 'playerNameLinks',
})`
  font-weight: ${({ isVisualFormat = false, isListFormat = false }) => (isVisualFormat || isListFormat ? 900 : 'bold')};
  font-size: ${({ isVisualFormat = false, isListFormat = false }) =>
    isListFormat ? '17px' : isVisualFormat ? '15px' : '13px'};
  border-bottom: solid 1px transparent;
  display: inline-block;
  margin-top: ${({ simpleInline }) => (simpleInline ? '1px' : 0)};
  color: ${({ isListFormat = false, type }) =>
    isListFormat && type === PlayerMatchupTypes.PITCHERPOSTGAME ? mlbBlack : ''};
`;

export const PlayerMatchupHeadshotWrapper = styled.div``;

interface PlayerDecisionWrapper {
  playerStatsInline: boolean;
  isListFormat: boolean;
  isVisualFormat: boolean;
  dark: boolean;
}

// Player decision is ALWAYS align left so don't need to account for various margins here
export const PlayerDecisionWrapper = styled.span<PlayerDecisionWrapper>`
  font-weight: bold;
  font-size: 13px;
  margin-right: 3px;
  color: ${mlbGrey5};
  width: ${({ playerStatsInline }) => (playerStatsInline ? '13px' : 'auto')};
  padding-right: ${({ playerStatsInline }) => (playerStatsInline ? '6px' : '0')};
  ${({ isListFormat }) =>
    isListFormat
      ? `
      color: ${mlbBlack};
      font-weight: 900;
      font-size: 17px;
    `
      : ''}
`;

export const PlayerDecisionAltWrapper = styled(PlayerDecisionWrapper)<PlayerInfoProps>`
  border: 1px solid ${({ dark }) => (dark ? mlbWhite : mlbBlack)};
  color: ${({ dark }) => (dark ? mlbWhite : mlbBlack)};
  padding: 1px 4px;
  margin-right: 5px;
  border-radius: 5px;
  box-sizing: border-box;
`;

interface PlayerPregameWrapperProps {
  alignment: 'left' | 'right';
  isListFormat: boolean;
  isVisualFormat: boolean;
}

export const PlayerPregameWrapper = styled.span<PlayerPregameWrapperProps>`
  margin-left: ${({ alignment }) => (alignment === 'left' ? '.25em' : 0)};
  margin-right: ${({ alignment }) => (alignment === 'left' ? 0 : '.25em')};
  ${({ isVisualFormat }) =>
    isVisualFormat
      ? `
      color: ${mlbGrey5};
      font-weight: 900;
      font-size: 15px;
    `
      : ''}
  ${({ isListFormat }) =>
    isListFormat
      ? `
      color: ${mlbGrey5};
      font-weight: 900;
      font-size: 17px;
    `
      : ''}
`;
